import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Autocomplete,
    Button,
    IconButton,
    Typography,
    Box,
    Avatar,
    Grid,
    Collapse,
    Alert,
    Divider,
    CircularProgress
} from '@mui/material';
import { PiBuildingsLight as OrganizationIcon } from "react-icons/pi";
import { IoClose as Close } from 'react-icons/io5';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getCountries, getStates } from 'country-state-picker';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import ErrorHandler from '@Root/ErrorHandler';
import OrganizationCardService from '@Helpers/URLHelpers/WeboutService/OrganizationCardService';
import './OrganizationCardForm.scss';

export default function OrganizationCardForm(props) {
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [orgState, setOrgState] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [states, setStates] = useState([]);
    const [orgCountry, setOrgCountry] = useState(null);
    const [orgIndustry, setOrgIndustry] = useState('');
    const [country, setCountry] = useState('');
    const [pinCode, setPinCode] = useState('');
    const [photo, setPhoto] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [uploadStatus, setUploadStatus] = useState('');
    const [remarks, setRemarks] = useState('');
    const [showAdvanced, setShowAdvanced] = useState(false);

    const countries = getCountries();
    const industries = [
        "Agriculture, Forestry, Fishing, and Hunting",
        "Mining and Quarrying",
        "Construction",
        "Manufacturing",
        "Utilities",
        "Wholesale Trade",
        "Retail Trade",
        "Transportation and Warehousing",
        "Information",
        "Finance and Insurance",
        "Real Estate and Rental and Leasing",
        "Professional, Scientific, and Technical Services",
        "Management of Companies and Enterprises",
        "Administrative and Support Services",
        "Educational Services",
        "Healthcare and Social Assistance",
        "Arts, Entertainment, and Recreation",
        "Accommodation and Food Services",
        "Other Services",
        "Public Administration"
    ];

    const errorHandler = new ErrorHandler();
    const organizationCardService = new OrganizationCardService();
    const { currentProjectId } = useReduxProject();
    const className = 'organizationCardForm';

    const handleNameChange = (e) => {
        setName(DOMPurify.sanitize(e.target.value));
    };

    const handleAddressChange = (e) => {
        setAddress(DOMPurify.sanitize(e.target.value));
    };

    const handleRemarksChange = (e) => {
        setRemarks(DOMPurify.sanitize(e.target.value));
    };

    const handleStateChange = (e, value) => {
        setOrgState(value || '');
    };

    const handleIndustryChange = (e, value) => {
        setOrgIndustry(value || '');
    };

    const handlePinCodeChange = (e) => {
        const re = /^[0-9\b]+$/;
        if (e.target.value === '' || re.test(e.target.value)) {
            setPinCode(e.target.value);
        }
    };

    const handleCountryChange = (e, value) => {
        if (value) {
            setStates(getStates(value.code));
            setCountry(value.name);
            setCountryCode(value.code);
            setOrgCountry(value);
            setOrgState('');
        }
    };

    const handleFileChange = async (e) => {
        e?.preventDefault();
        const file = e.target.files[0];
        if (file) {
            if (validateImage(file)) {
                const base64File = await convertFileToBase64(file);
                setPhoto(base64File);
                setImageUrl(URL.createObjectURL(file));
                setUploadStatus('');
            }
        }
    };

    const validateImage = (file) => {
        if (file.type.split('/')[0] === 'image') {
            const sizeLimit = 1024 * 1024;
            if (file.size > sizeLimit) {
                setUploadStatus('Image size exceeded 1MB!');
                return false;
            }
            return true;
        } else {
            setUploadStatus('Upload image file!');
            return false;
        }
    };

    const convertFileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });

    const handleSubmit = async (e) => {
        try {
            e?.preventDefault();
            setLoading(true);
            const orgCardObj = {
                projectId: currentProjectId,
                name,
                industryType: showAdvanced ? orgIndustry : undefined,
                address: showAdvanced ? address : undefined,
                state: showAdvanced ? orgState : undefined,
                country: showAdvanced ? country : undefined,
                countryCode: showAdvanced ? countryCode : undefined,
                pinCode: showAdvanced ? pinCode : undefined,
                photo,
                remarks: showAdvanced ? remarks : undefined
            };
            await organizationCardService.createOrganizationCard(orgCardObj);
            await props.reloadData();
            props.closePopup();
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={props.open}
            onClose={props.closePopup}
            maxWidth="md"
            fullWidth
            className={className}
            PaperProps={{
                className: `${className}__paper`
            }}
        >
            <DialogTitle className={`${className}__header`}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <OrganizationIcon className={`${className}__header__icon`} />
                        <Typography variant="h6" className={`${className}__header__title`}>
                            Create Organization Card
                        </Typography>
                    </Box>
                    <IconButton onClick={props.closePopup} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <Divider />

            <DialogContent className={`${className}__content`}>
                <form onSubmit={handleSubmit} id="org-form">
                    {/* Profile Section */}
                    <Box className={`${className}__section`}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={8}>
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <TextField
                                        label="Organization Name"
                                        placeholder="Enter organization name"
                                        value={name}
                                        onChange={handleNameChange}
                                        required
                                        fullWidth
                                        inputProps={{ maxLength: 200 }}
                                        helperText={
                                            name.length >= 180 ? `${name.length}/200 characters` : ''
                                        }
                                        FormHelperTextProps={{
                                            className: name.length >= 200 ? `${className}__charLimit` : ''
                                        }}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box className={`${className}__photoSection`}>
                                    <input
                                        type="file"
                                        accept="image/jpeg, image/png, image/webp"
                                        id="fileInput"
                                        onChange={handleFileChange}
                                        style={{ display: 'none' }}
                                    />
                                    <label htmlFor="fileInput">
                                        <Box className={`${className}__photoContainer`}>
                                            <Avatar
                                                className={`${className}__avatar`}
                                                src={imageUrl}
                                            >
                                                <OrganizationIcon className={`${className}__avatarIcon`} />
                                            </Avatar>
                                            <IconButton
                                                component="span"
                                                className={`${className}__photoBtn`}
                                            >
                                                <PhotoCameraIcon />
                                            </IconButton>
                                        </Box>
                                    </label>
                                    <Typography variant="caption" color="textSecondary" align="center">
                                        Click to upload logo
                                    </Typography>
                                    {uploadStatus && (
                                        <Alert severity="error" className={`${className}__uploadError`}>
                                            {uploadStatus}
                                        </Alert>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Advanced Details Toggle */}
                    <Box className={`${className}__toggleSection`}>
                        <Button
                            variant="text"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            className={`${className}__toggleBtn`}
                        >
                            {showAdvanced ? 'Hide' : 'Show'} Additional Details
                        </Button>
                    </Box>

                    {/* Advanced Section */}
                    <Collapse in={showAdvanced}>
                        <Box className={`${className}__section`}>
                            <Typography variant="subtitle2" className={`${className}__sectionTitle`}>
                                Business Information
                            </Typography>
                            <Autocomplete
                                options={industries}
                                value={orgIndustry}
                                onChange={handleIndustryChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Industry Type"
                                        placeholder="Select industry"
                                    />
                                )}
                            />
                        </Box>

                        <Box className={`${className}__section`}>
                            <Typography variant="subtitle2" className={`${className}__sectionTitle`}>
                                Location Information
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Address"
                                        placeholder="Enter address"
                                        value={address}
                                        onChange={handleAddressChange}
                                        multiline
                                        rows={4}
                                        fullWidth
                                        inputProps={{ maxLength: 200 }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box display="flex" flexDirection="column" gap={2}>
                                        <TextField
                                            label="Pin Code"
                                            placeholder="Enter pin code"
                                            value={pinCode}
                                            onChange={handlePinCodeChange}
                                            fullWidth
                                            inputProps={{ maxLength: 20 }}
                                        />
                                        <Autocomplete
                                            options={countries}
                                            value={orgCountry}
                                            onChange={handleCountryChange}
                                            getOptionLabel={(option) => option.name}
                                            isOptionEqualToValue={(option, value) => option.code === value.code}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Country" placeholder="Select country" />
                                            )}
                                        />
                                        <Autocomplete
                                            options={states}
                                            value={orgState}
                                            onChange={handleStateChange}
                                            disabled={!orgCountry}
                                            renderInput={(params) => (
                                                <TextField {...params} label="State" placeholder="Select state" />
                                            )}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>

                        <Box className={`${className}__section`}>
                            <Typography variant="subtitle2" className={`${className}__sectionTitle`}>
                                Additional Notes
                            </Typography>
                            <TextField
                                label="Remarks"
                                placeholder="Enter any additional remarks"
                                value={remarks}
                                onChange={handleRemarksChange}
                                multiline
                                rows={4}
                                fullWidth
                            />
                        </Box>
                    </Collapse>
                </form>
            </DialogContent>

            <Divider />

            <DialogActions className={`${className}__footer`}>
                <Button
                    onClick={props.closePopup}
                    disabled={loading}
                    variant="outlined"
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    form="org-form"
                    variant="contained"
                    disabled={loading}
                    startIcon={loading && <CircularProgress size={16} />}
                >
                    {loading ? 'Creating...' : 'Create Organization'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
